<script setup lang="ts">
import { computed, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';
import { createI18nMessage, required } from '@vuelidate/validators';
import { isValidPhoneNumber } from 'libphonenumber-js';
import {
  BaseButton,
  FieldInput,
  FieldPhoneNumber,
  FieldSelect,
} from '@magma-app/magma-lapilli';
import type { CampaignCriteria } from '@/composables/helpee-signup/useHelpeeCriteria';

type CriteriaValue = string | string[] | null;

const props = defineProps<{
  strongCriteria?: CampaignCriteria;
  criteriaOptions: { label: string; value: string }[];
  initialValues: {
    firstname: string;
    lastname: string;
    phoneNumber: string;
    criteria: CriteriaValue;
  };
  phoneRequired: boolean;
  primaryColor?: string;
  rgbColor?: string;
  isLoading?: boolean;
  // Backend rejection of the number itself (e.g. blocked country), shown under the field
  phoneError?: string | null;
}>();

const emit = defineEmits<{
  (
    e: 'submit',
    payload: {
      firstname: string;
      lastname: string;
      phoneNumber: string;
      criteria: CriteriaValue;
    }
  ): void;
  (e: 'criteria-change'): void;
  (e: 'phone-change'): void;
}>();

const { t } = useI18n({ useScope: 'global' });
const withI18nMessage = createI18nMessage({ t });

const formData = reactive({
  firstname: '',
  lastname: '',
  phoneNumber: '',
  criteria: null as CriteriaValue,
});

watch(
  () => props.initialValues,
  (values) => {
    formData.firstname = values.firstname ?? '';
    formData.lastname = values.lastname ?? '';
    formData.phoneNumber = values.phoneNumber ?? '';
    formData.criteria = values.criteria ?? null;
  },
  { immediate: true, deep: true }
);

const validationRules = {
  firstname: {
    required: withI18nMessage(required, {
      messagePath: () => t('validation.value'),
    }),
  },
  lastname: {
    required: withI18nMessage(required, {
      messagePath: () => t('validation.value'),
    }),
  },
  criteria: {
    required: withI18nMessage(required, {
      messagePath: () => t('validation.value'),
    }),
  },
  phoneNumber: {
    required: withI18nMessage(required, {
      messagePath: () => t('validation.value'),
    }),
    validPhoneNumber: withI18nMessage(
      (value: string) => {
        if (!value || value.length === 0) {
          return true;
        }
        return isValidPhoneNumber(value);
      },
      {
        messagePath: () => t('generic.phoneNumber') + t('validation.valid'),
      }
    ),
  },
};

const v$ = useVuelidate(validationRules, formData);

watch(
  () => formData.firstname,
  async () => await v$.value.firstname.$validate()
);

watch(
  () => formData.lastname,
  async () => await v$.value.lastname.$validate()
);

watch(
  () => formData.phoneNumber,
  async () => await v$.value.phoneNumber.$validate()
);

const isSubmitDisabled = computed(() => {
  return v$.value.$invalid || Boolean(props.isLoading);
});

const onCriteriaChange = () => {
  emit('criteria-change');
};

const onCriteriaBlur = async () => {
  await v$.value.criteria.$validate();
};

const onSubmit = async () => {
  if (isSubmitDisabled.value) {
    return;
  }

  v$.value.$touch();
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  emit('submit', {
    firstname: formData.firstname,
    lastname: formData.lastname,
    phoneNumber: formData.phoneNumber,
    criteria: formData.criteria,
  });
};
</script>

<template>
  <div class="relative h-full flex-1">
    <h3 class="text-mgm-txt-lg font-medium text-foreground-emphasis">
      {{ $t('helpeeCampaignSignUpView.yourInfo') }}
    </h3>

    <FieldInput
      id="firstname"
      v-model="formData.firstname"
      data-cy="firstname"
      class="mt-4"
      :locale="$i18n.locale"
      :label="$t('generic.firstNameLabel')"
      placeholder="John"
      required
      :validation="v$.firstname"
      :custom-color="primaryColor"
      :custom-rgb-color="rgbColor || primaryColor"
    />

    <FieldInput
      id="lastname"
      v-model="formData.lastname"
      data-cy="lastname"
      class="mt-4"
      :locale="$i18n.locale"
      :label="$t('generic.lastNameLabel')"
      placeholder="Doe"
      required
      :validation="v$.lastname"
      :custom-color="primaryColor"
      :custom-rgb-color="rgbColor || primaryColor"
    />

    <FieldPhoneNumber
      id="phone"
      :model-value="formData.phoneNumber"
      data-cy="phone"
      class="mt-4"
      :locale="$i18n.locale"
      :label="$t('generic.phone')"
      required
      :validation="v$.phoneNumber"
      :custom-color="primaryColor"
      :custom-rgb-color="rgbColor || primaryColor"
      :description="$t('helpeeCampaignSignUpView.sendSMS')"
      :error-message="phoneError"
      @update:model-value="
        (value) => {
          formData.phoneNumber = value ?? '';
          emit('phone-change');
        }
      "
    />

    <FieldSelect
      v-if="strongCriteria"
      :id="strongCriteria.id"
      v-model="formData.criteria"
      data-cy="criteria"
      class="mt-4"
      :custom-color="primaryColor"
      :custom-rgb-color="rgbColor || primaryColor"
      :label="strongCriteria.label"
      :options="criteriaOptions"
      :multiple="strongCriteria.type === 'multiSelect'"
      :validation="v$.criteria"
      required
      @change="onCriteriaChange"
      @blur="onCriteriaBlur"
    />

    <BaseButton
      data-cy="signup-next"
      class="mt-8 w-full"
      :custom-color="primaryColor"
      :custom-rgb-color="rgbColor || primaryColor"
      :disabled="isSubmitDisabled"
      :is-loading="isLoading"
      :text="$t('generic.next')"
      size="md"
      @click="onSubmit"
    />
  </div>
</template>
